import { api } from "./client";
import { apiRoutes } from "util/apiRoutes";
import type {
  ChatConversation,
  ChatConversationListResponse,
  ChatMessage,
  ChatMessageListResponse,
  ChatRecipientSearchResult,
  ChatRequestListResponse,
  ChatSettings,
  ChatUnreadCounts,
  CreateChatConversationInput,
  CreateChatMediaPresignInput,
  CreateChatMediaPresignResponse,
  FinalizeChatMediaInput,
  FinalizeChatMediaResponse,
  SendChatMessageInput,
  ShareChatEntityInput,
  ShareChatEntityResponse,
} from "types/chat";

interface ChatRequestActionResponse {
  request: Record<string, unknown>;
  conversation: ChatConversation | null;
}

export const chatApi = {
  getConversations: async (params?: {
    bucket?: string;
    search?: string;
    limit?: number;
    offset?: number;
  }): Promise<ChatConversationListResponse> => {
    const response = await api.get<ChatConversationListResponse>(apiRoutes.chat.conversations, { params });
    return response.data;
  },
  createConversation: async (payload: CreateChatConversationInput): Promise<ChatConversation> => {
    const response = await api.post<ChatConversation>(apiRoutes.chat.conversations, payload);
    return response.data;
  },
  getConversation: async (publicId: string): Promise<ChatConversation> => {
    const response = await api.get<ChatConversation>(apiRoutes.chat.conversation(publicId));
    return response.data;
  },
  updateConversation: async (
    publicId: string,
    payload: {
      title?: string | null;
      hidden?: boolean;
      pinned?: boolean;
      muted_until?: string | null;
    }
  ): Promise<ChatConversation> => {
    const response = await api.patch<ChatConversation>(apiRoutes.chat.conversation(publicId), payload);
    return response.data;
  },
  archiveConversation: async (publicId: string): Promise<void> => {
    await api.delete(apiRoutes.chat.conversation(publicId));
  },
  getMessages: async (publicId: string, params?: { before?: string; limit?: number }): Promise<ChatMessageListResponse> => {
    const response = await api.get<ChatMessageListResponse>(apiRoutes.chat.messages(publicId), { params });
    return response.data;
  },
  sendMessage: async (publicId: string, payload: SendChatMessageInput): Promise<{ conversation: ChatConversation; message: ChatMessage }> => {
    const response = await api.post<{ conversation: ChatConversation; message: ChatMessage }>(apiRoutes.chat.messages(publicId), payload);
    return response.data;
  },
  shareEntity: async (payload: ShareChatEntityInput): Promise<ShareChatEntityResponse> => {
    const response = await api.post<ShareChatEntityResponse>(apiRoutes.chat.share, payload);
    return response.data;
  },
  editMessage: async (
    messagePublicId: string,
    payload: Pick<SendChatMessageInput, "body_html" | "body_rich">
  ): Promise<ChatMessage> => {
    const response = await api.patch<ChatMessage>(apiRoutes.chat.message(messagePublicId), payload);
    return response.data;
  },
  deleteMessage: async (messagePublicId: string): Promise<ChatMessage> => {
    const response = await api.delete<ChatMessage>(apiRoutes.chat.message(messagePublicId));
    return response.data;
  },
  getThread: async (messagePublicId: string): Promise<ChatMessageListResponse> => {
    const response = await api.get<ChatMessageListResponse>(apiRoutes.chat.thread(messagePublicId));
    return response.data;
  },
  replyInThread: async (messagePublicId: string, payload: SendChatMessageInput): Promise<{ conversation: ChatConversation; message: ChatMessage }> => {
    const response = await api.post<{ conversation: ChatConversation; message: ChatMessage }>(apiRoutes.chat.thread(messagePublicId), payload);
    return response.data;
  },
  addReaction: async (messagePublicId: string, reactionKey: string): Promise<ChatMessage> => {
    const response = await api.post<ChatMessage>(apiRoutes.chat.reactions(messagePublicId), { reaction_key: reactionKey });
    return response.data;
  },
  removeReaction: async (messagePublicId: string, reactionKey: string): Promise<ChatMessage> => {
    const response = await api.delete<ChatMessage>(apiRoutes.chat.reaction(messagePublicId, reactionKey));
    return response.data;
  },
  createMediaPresignUrls: async (publicId: string, payload: CreateChatMediaPresignInput): Promise<CreateChatMediaPresignResponse> => {
    const response = await api.post<CreateChatMediaPresignResponse>(apiRoutes.chat.mediaPresign(publicId), payload);
    return response.data;
  },
  finalizeMediaUpload: async (publicId: string, payload: FinalizeChatMediaInput): Promise<FinalizeChatMediaResponse> => {
    const response = await api.post<FinalizeChatMediaResponse>(apiRoutes.chat.media(publicId), payload);
    return response.data;
  },
  getRequests: async (params?: { limit?: number; offset?: number }): Promise<ChatRequestListResponse> => {
    const response = await api.get<ChatRequestListResponse>(apiRoutes.chat.requests, { params });
    return response.data;
  },
  acceptRequest: async (requestId: number): Promise<ChatRequestActionResponse> => {
    const response = await api.post<ChatRequestActionResponse>(apiRoutes.chat.requestAccept(requestId));
    return response.data;
  },
  declineRequest: async (requestId: number): Promise<ChatRequestActionResponse> => {
    const response = await api.post<ChatRequestActionResponse>(apiRoutes.chat.requestDecline(requestId));
    return response.data;
  },
  blockRequest: async (requestId: number): Promise<ChatRequestActionResponse> => {
    const response = await api.post<ChatRequestActionResponse>(apiRoutes.chat.requestBlock(requestId));
    return response.data;
  },
  markRead: async (publicId: string, lastReadMessagePublicId: string): Promise<void> => {
    await api.post(apiRoutes.chat.read(publicId), {
      last_read_message_public_id: lastReadMessagePublicId,
    });
  },
  getUnreadCount: async (): Promise<ChatUnreadCounts> => {
    const response = await api.get<ChatUnreadCounts>(apiRoutes.chat.unreadCount);
    return response.data;
  },
  getSettings: async (): Promise<ChatSettings> => {
    const response = await api.get<ChatSettings>(apiRoutes.chat.settings);
    return response.data;
  },
  updateSettings: async (payload: Partial<ChatSettings> & {
    notification_type?: string;
    active?: boolean;
    channel?: string;
  }): Promise<ChatSettings> => {
    const response = await api.patch<ChatSettings>(apiRoutes.chat.settings, payload);
    return response.data;
  },
  searchRecipients: async (query: string): Promise<ChatRecipientSearchResult[]> => {
    const trimmed = query.trim();
    if (!trimmed) {
      return [];
    }

    const [usersResponse, teamsResponse] = await Promise.all([
      api.get<Array<{
        id: number | string;
        username: string;
        display_name: string;
        properties_public?: Record<string, unknown>;
      }>>(apiRoutes.user.search, { params: { q: trimmed, limit: 8 } }),
      api.get<Array<{
        id: number | string;
        teamname: string;
        display_name: string;
        properties_public?: Record<string, unknown>;
      }>>(apiRoutes.team.root, { params: { q: trimmed, limit: 8 } }),
    ]);

    const users = Array.isArray(usersResponse.data)
      ? usersResponse.data.map((user) => ({
          type: "user" as const,
          id: Number(user.id),
          username: user.username,
          display_name: user.display_name,
          properties_public: user.properties_public || {},
        }))
      : [];
    const teams = Array.isArray(teamsResponse.data)
      ? teamsResponse.data.map((team) => ({
          type: "team" as const,
          id: Number(team.id),
          teamname: team.teamname,
          display_name: team.display_name,
          properties_public: team.properties_public || {},
        }))
      : [];

    return [...users, ...teams];
  },
};
